import { ParsedUrlQuery } from 'querystring';
import { formatMoneyObject } from 'shared/utils/formatMoneyObject';
import { FILTER_ENUMS } from 'search/enums/constants';
import { getImageSrcs } from 'shared/utils/getImageSrcs';
import { IConstructorBrowseResponse } from 'shared/types/ConstructorBrowseResponse';
import { LOCALES } from 'localization/constants/configEnums';
import { getLowestPriceCents } from './getLowestPriceCents';

interface IGetCollectionPageSchemaParams {
  constructorResponse: IConstructorBrowseResponse;
  locale: string;
  asPath: string;
  description: string;
  silhouette: string;
  selectedCurrencyCode: string;
  globalPricingId: string;
  query: ParsedUrlQuery;
}

export const getCollectionPageSchema = ({
  constructorResponse,
  locale,
  asPath,
  description,
  silhouette,
  selectedCurrencyCode,
  globalPricingId,
  query,
}: IGetCollectionPageSchemaParams) => {
  const getLocalizedURL = (path: string) => {
    const baseURL = 'https://www.goat.com';
    if (locale !== LOCALES.enUS) {
      return `${baseURL}/${locale}${path}`;
    }
    return `${baseURL}${path}`;
  };

  const getItemListElements = () => {
    const itemListElements = [];

    const priceFacet = constructorResponse?.response?.facets?.find(
      (facet) => facet.name === FILTER_ENUMS.PRICE,
    );
    const currencyRates = priceFacet?.data?.currency_rates || null;

    constructorResponse?.response?.results?.forEach((result, index) => {
      const { src } = getImageSrcs({
        src: result?.data?.image_url,
        sizeWidthDisabled: true,
      });

      const { price } = getLowestPriceCents({
        selectedCurrencyCode,
        result,
        currencyRates,
        globalPricingId,
        query,
      });

      const lowPrice = formatMoneyObject(
        { currency: selectedCurrencyCode, amount: price },
        { showCents: true, displayCurrencySymbol: false, useGrouping: false },
      );

      if (lowPrice) {
        itemListElements.push({
          '@type': 'ListItem',
          position: index + 1,
          item: {
            '@type': 'Product',
            name: result?.value,
            image: src || result?.data?.image_url,
            url: getLocalizedURL(`/sneakers/${result?.data?.slug}`),
            offers: {
              '@type': 'AggregateOffer',
              lowPrice: Number.parseFloat(lowPrice),
              priceCurrency: selectedCurrencyCode,
            },
          },
        });
      }
    });

    return itemListElements;
  };

  return JSON.stringify({
    '@context': 'http://schema.org',
    '@type': 'CollectionPage',
    name: silhouette,
    url: getLocalizedURL(asPath),
    description,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: getItemListElements(),
    },
  });
};
